import { ref, computed, Ref } from 'vue';
import type {
  FlowStructureDto,
  FlowStepDto,
  FlowConnection,
} from '@/types/flow-structure';
import type { FlowOperatorMetadata } from '@/types/flow-types';
import { getOperatorById, OperatorInsertContext } from '@/components/flows/operators/registry';
import { store } from '@/store';
import type { OpenFlow, FlowIdentifier } from '@/store/modules/flow-editor';
import { useFlowHistory, FlowActionType, HistoryEntryMeta } from '@/composables/flows/useFlowHistory';
import { i18n } from '@/i18n';

/**
 * Context for adding a new operator to the flow
 */
export interface AddOperatorPayload {
  operator: FlowOperatorMetadata;
  data: Record<string, any>;
  context: OperatorInsertContext;
  targetStepId?: string;
}

/**
 * Extract the numeric part from a created_operator_X ID
 */
const extractOperatorNumber = (id: string): number => {
  const match = id.match(/^created_operator_(\d+)$/);
  return match ? parseInt(match[1], 10) : 0;
};

/**
 * Generate the next operator ID based on existing steps in the flow
 * Format: created_operator_X where X is max existing + 1
 */
const generateNextOperatorId = (flow: FlowStructureDto | null): string => {
  if (!flow || !flow.steps || flow.steps.length === 0) {
    return 'created_operator_1';
  }

  let maxNumber = 0;
  flow.steps.forEach((step) => {
    const num = extractOperatorNumber(step.id);
    if (num > maxNumber) {
      maxNumber = num;
    }
  });

  return `created_operator_${maxNumber + 1}`;
};

/**
 * Convert operator form data to parameters object format
 */
const dataToParameters = (operatorId: string, data: Record<string, any>): Record<string, string> => {
  const parameters: Record<string, string> = {
    id: operatorId,
  };

  for (const [key, value] of Object.entries(data)) {
    if (typeof value === 'object') {
      parameters[key] = JSON.stringify(value);
    } else {
      parameters[key] = String(value);
    }
  }

  return parameters;
};

/**
 * Map operator kind to class name
 */
const kindToClass = (kind: string): string => {
  switch (kind) {
    case 'trigger': return 'declencheur';
    case 'filter': return 'filtre';
    case 'action': return 'action';
    default: return 'action';
  }
};

// [FLOW_EDITOR_DEBUG] Prefix for all debug logs - search for this to remove later
const LOG_PREFIX = '[FLOW_EDITOR_DEBUG]';

/**
 * Get translated label for an operator by its ID and parameters
 * Uses getDisplayLabel if available (for custom titles), otherwise falls back to translated label
 * Exported for use in components that need to display operator labels
 */
export const getOperatorLabel = (operatorId: string, parameters?: Record<string, any>): string => {
  const metadata = getOperatorById(operatorId);
  if (!metadata) return operatorId;

  const t = i18n.global.t;
  
  // Try getDisplayLabel first (for custom titles like custom_title)
  if (metadata.getDisplayLabel && parameters) {
    const dynamicLabel = metadata.getDisplayLabel(parameters, t);
    if (dynamicLabel) return dynamicLabel;
  }
  
  // Fall back to translated label
  if (metadata.label) {
    return t(metadata.label) as string;
  }
  
  return operatorId;
};

/**
 * Format history meta label based on action type
 * Shared utility for history display in FlowEditorModal and FlowHistoryPanel
 * @param actionType The type of history action
 * @param meta The history entry metadata
 * @param snapshot Optional flow snapshot to retrieve operator parameters for getDisplayLabel
 */
export const formatHistoryMetaLabel = (actionType: string, meta?: HistoryEntryMeta, snapshot?: FlowStructureDto): string | null => {
  if (!meta) return null;

  switch (actionType) {
    case 'add_operator':
    case 'remove_operator':
    case 'update_operator':
    case 'reorder_branches':
      if (meta.operatorType) {
        return getOperatorLabel(meta.operatorType);
      }
      break;
    case 'replace_operator':
      if (meta.oldOperatorType && meta.newOperatorType) {
        return `${getOperatorLabel(meta.oldOperatorType)} → ${getOperatorLabel(meta.newOperatorType)}`;
      }
      break;
    case 'merge_branches':
    case 'unmerge_branches':
      if (meta.sourceOperatorType && meta.targetOperatorType) {
        return `${getOperatorLabel(meta.sourceOperatorType)} → ${getOperatorLabel(meta.targetOperatorType)}`;
      }
      break;
  }

  return null;
};

/**
 * Composable for managing flow editor state for a specific flow
 * 
 * Architecture:
 * - Uses flowEditor store for global state management (multi-flow support)
 * - Each call with a flowIdentifier returns an editor bound to that flow
 * - All mutations go through the store for consistency
 * - Supports minimize/restore for multi-document editing
 * 
 * Usage:
 * ```
 * // For a specific flow (bound to store)
 * const editor = useFlowEditor({ id: 123, type: TypeCampaignEnum.AUTOMATION });
 * editor.flow.value // FlowStructureDto
 * editor.isDirty.value // boolean
 * 
 * // For standalone usage (local state only)
 * const editor = useFlowEditor();
 * editor.initFlow(data);
 * ```
 */
export function useFlowEditor(flowIdentifier?: FlowIdentifier) {
  // Local state for standalone mode (when no flowIdentifier)
  const localFlow: Ref<FlowStructureDto | null> = ref(null);
  const localIsDirty = ref(false);

  // History management
  const history = useFlowHistory({ maxSize: 50, debounceMs: 300 });

  // Get flow from store (if flowIdentifier provided)
  const getOpenFlow = (): OpenFlow | null => {
    if (!flowIdentifier) return null;
    return store.getters['flowEditor/getFlowById'](flowIdentifier.id, flowIdentifier.type);
  };

  // Restore history from store if available (for minimize/restore persistence)
  if (flowIdentifier) {
    const openFlow = getOpenFlow();
    if (openFlow?.history) {
      history.setState(openFlow.history);
    }
  }

  // Helper to persist history to store
  const persistHistoryToStore = (): void => {
    if (flowIdentifier) {
      store.commit('flowEditor/updateFlowHistory', {
        id: flowIdentifier.id,
        type: flowIdentifier.type,
        history: history.getState(),
      });
    }
  };

  // Flow getter - from store or local
  const flow = computed(() => {
    const scenario = getOpenFlow();
    return scenario ? scenario.flowData : localFlow.value;
  });

  // isDirty getter - from store or local
  const isDirty = computed(() => {
    const scenario = getOpenFlow();
    return scenario ? scenario.isDirty : localIsDirty.value;
  });

  // Helper to get mutable flow reference
  const getMutableFlow = (): FlowStructureDto | null => {
    const scenario = getOpenFlow();
    return scenario ? scenario.flowData : localFlow.value;
  };

  // Helper to mark flow as modified and trigger a store update
  const markDirty = (): void => {
    if (flowIdentifier) {
      const currentFlow = getMutableFlow();
      if (currentFlow) {
        store.commit('flowEditor/updateFlowData', {
          id: flowIdentifier.id,
          type: flowIdentifier.type,
          flowData: currentFlow,
        });
      }
    } else {
      localIsDirty.value = true;
    }
  };

  /**
   * Save current flow state to history before a mutation
   */
  const saveToHistory = (actionType: FlowActionType, meta?: HistoryEntryMeta): void => {
    const currentFlow = getMutableFlow();
    if (currentFlow) {
      history.pushSnapshot(currentFlow, actionType, meta);
      persistHistoryToStore();
    }
  };

  /**
   * Create a save checkpoint in history after a successful save
   * This marks a point the user can undo back to and clears the redo stack
   */
  const createSaveCheckpoint = (): void => {
    const currentFlow = getMutableFlow();
    if (currentFlow) {
      // Clear redo stack since we're creating a new save point
      history.clearRedoStack();
      history.pushSnapshot(currentFlow, 'save', undefined, true);
      persistHistoryToStore();
    }
  };

  /**
   * Undo the last action and restore previous flow state
   */
  const undo = (): boolean => {
    const currentFlow = getMutableFlow();
    if (!currentFlow || !history.canUndo.value) return false;

    const previousState = history.undo(currentFlow);
    if (previousState) {
      if (flowIdentifier) {
        store.commit('flowEditor/updateFlowData', {
          id: flowIdentifier.id,
          type: flowIdentifier.type,
          flowData: previousState,
        });
      } else {
        localFlow.value = previousState;
      }
      console.log(LOG_PREFIX, 'UNDO', { canUndo: history.canUndo.value, canRedo: history.canRedo.value });
      persistHistoryToStore();
      return true;
    }
    return false;
  };

  /**
   * Redo the last undone action
   */
  const redo = (): boolean => {
    const currentFlow = getMutableFlow();
    if (!currentFlow || !history.canRedo.value) return false;

    const nextState = history.redo(currentFlow);
    if (nextState) {
      if (flowIdentifier) {
        store.commit('flowEditor/updateFlowData', {
          id: flowIdentifier.id,
          type: flowIdentifier.type,
          flowData: nextState,
        });
      } else {
        localFlow.value = nextState;
      }
      console.log(LOG_PREFIX, 'REDO', { canUndo: history.canUndo.value, canRedo: history.canRedo.value });
      persistHistoryToStore();
      return true;
    }
    return false;
  };

  /**
   * Restore to a specific history entry by index
   */
  const restoreToIndex = (index: number): boolean => {
    const currentFlow = getMutableFlow();
    if (!currentFlow) return false;

    const restoredState = history.restoreToIndex(index, currentFlow);
    if (restoredState) {
      if (flowIdentifier) {
        store.commit('flowEditor/updateFlowData', {
          id: flowIdentifier.id,
          type: flowIdentifier.type,
          flowData: restoredState,
        });
      } else {
        localFlow.value = restoredState;
      }
      console.log(LOG_PREFIX, 'RESTORE_TO_INDEX', { index, canUndo: history.canUndo.value, canRedo: history.canRedo.value });
      persistHistoryToStore();
      return true;
    }
    return false;
  };

  /**
   * Initialize the editor with flow data (standalone mode only)
   */
  const initFlow = (data: FlowStructureDto): void => {
    if (flowIdentifier) {
      console.warn('initFlow should not be called when using store mode. Use store.dispatch("flowEditor/openFlow") instead.');
      return;
    }
    localFlow.value = JSON.parse(JSON.stringify(data));
    localIsDirty.value = false;
  };

  /**
   * Reset the editor state (standalone mode only)
   */
  const resetFlow = (): void => {
    if (flowIdentifier) {
      console.warn('resetFlow should not be called when using store mode. Use store.dispatch("flowEditor/closeFlow") instead.');
      return;
    }
    localFlow.value = null;
    localIsDirty.value = false;
  };

  /**
   * Get the current flow data for saving
   */
  const getFlowForSave = (): FlowStructureDto | null => {
    const currentFlow = getMutableFlow();
    return currentFlow ? JSON.parse(JSON.stringify(currentFlow)) : null;
  };

  /**
   * Find a step by ID
   */
  const findStep = (stepId: string): FlowStepDto | undefined => {
    const currentFlow = getMutableFlow();
    return currentFlow?.steps.find(s => s.id === stepId);
  };

  /**
   * Calculate the max depth of a step (Recursively from parents)
   * This is more robust than BFS from root because it doesn't rely on rootStepId
   */
  const calculateStepDepth = (flow: FlowStructureDto, stepId: string, cache = new Map<string, number>(), stack = new Set<string>()): number => {
    if (cache.has(stepId)) return cache.get(stepId)!;
    if (stack.has(stepId)) return 0; // Cycle detected

    const step = flow.steps.find(s => s.id === stepId);
    if (!step || !step.parents || step.parents.length === 0) {
      cache.set(stepId, 0);
      return 0;
    }

    stack.add(stepId);
    let maxParentDepth = -1;
    
    // Check all parents
    for (const parentRef of step.parents) {
      const pDepth = calculateStepDepth(flow, parentRef.id, cache, stack);
      if (pDepth > maxParentDepth) {
        maxParentDepth = pDepth;
      }
    }

    stack.delete(stepId);
    
    const depth = maxParentDepth + 1;
    cache.set(stepId, depth);
    // console.log('[FLOW_DEBUG] Step:', stepId, 'Depth:', depth);
    return depth;
  };

  /**
   * Find the deepest parent among a list of parent candidates
   */
  const findDeepestParent = (flow: FlowStructureDto, parents: Array<{ stepId: string, output: number }>): { stepId: string, output: number } | null => {
    if (parents.length === 0) return null;
    if (parents.length === 1) return parents[0];

    let deepestParent = parents[0];
    let maxDepth = -1;
    
    // Shared cache for this batch
    const cache = new Map<string, number>();

    parents.forEach(p => {
      const depth = calculateStepDepth(flow, p.stepId, cache);
      
      // If deeper, pick it
      if (depth > maxDepth) {
        maxDepth = depth;
        deepestParent = p;
      } 
      // Tie-breaker: Deterministic sort (e.g. by ID) to ensure order-independence
      // If depths are equal, we want a consistent winner regardless of selection order.
      else if (depth === maxDepth) {
        if (p.stepId > deepestParent.stepId) {
           deepestParent = p;
        }
      }
    });

    return deepestParent;
  };

  /**
   * Add a new operator to the flow
   */
  const addOperator = (payload: AddOperatorPayload): string | null => {
    const currentFlow = getMutableFlow();
    if (!currentFlow) return null;

    const { operator, data, context, targetStepId } = payload;

    // Generate step ID first so we can include it in history meta
    const newStepId = generateNextOperatorId(currentFlow);

    // Save state before mutation - use appropriate action type based on context
    const actionType = context.insertPosition === 'closure' ? 'closure_branches' : 'add_operator';
    // For closure, no need to show operator info
    const historyMeta: HistoryEntryMeta | undefined = context.insertPosition === 'closure' 
      ? undefined 
      : { operatorType: operator.id, operatorId: newStepId };
    saveToHistory(actionType, historyMeta);

    // Create the new step
    const newStep: FlowStepDto = {
      id: newStepId,
      type: operator.id,
      class: kindToClass(operator.kind),
      parents: [],
      children: [],
      parameters: dataToParameters(operator.id, data),
    };

    if (context.insertPosition === 'after' && targetStepId) {
      // Insert after target step
      const parentStep = findStep(targetStepId);
      const outputIndex = context.outputIndex ?? 1;
      if (parentStep) {
        // Update parent's children
        parentStep.children.push({ id: newStepId, output: outputIndex });

        // Set new step's parent
        newStep.parents = [{ id: targetStepId, output: outputIndex }];
        newStep.primaryParent = { id: targetStepId, output: outputIndex };

        // Add connection
        const newConnection: FlowConnection = {
          parentId: targetStepId,
          childId: newStepId,
          parentOutput: outputIndex,
        };
        currentFlow.connections.push(newConnection);
      }
    } else if (context.insertPosition === 'before' && targetStepId) {
      // Insert before target step (between all parents and target)
      const targetStep = findStep(targetStepId);
      if (targetStep && targetStep.parents && targetStep.parents.length > 0) {
        // Handle ALL parents of the target step (for closure nodes)
        const allParents = [...targetStep.parents];
        
        allParents.forEach((parentRef) => {
          const parentStep = findStep(parentRef.id);
          if (parentStep) {
            // Remove old connection parent -> target
            currentFlow.connections = currentFlow.connections.filter(
              c => !(c.parentId === parentRef.id && c.childId === targetStepId)
            );

            // Update parent's children: replace target with new step
            const childIndex = parentStep.children.findIndex(c => c.id === targetStepId);
            if (childIndex !== -1) {
              parentStep.children[childIndex] = { id: newStepId, output: parentRef.output };
            }

            // Add to new step's parents
            newStep.parents.push({ id: parentRef.id, output: parentRef.output });

            // Add connection from parent to new step
            currentFlow.connections.push({
              parentId: parentRef.id,
              childId: newStepId,
              parentOutput: parentRef.output,
            });
          }
        });

        // Set primary parent as the first one
        if (newStep.parents.length > 0) {
          newStep.primaryParent = newStep.parents[0];
        }

        // Set new step's children
        newStep.children = [{ id: targetStepId, output: 1 }];

        // Update target's parents to point to new step only
        targetStep.primaryParent = { id: newStepId, output: 1 };
        targetStep.parents = [{ id: newStepId, output: 1 }];

        // Add connection from new step to target
        currentFlow.connections.push({
          parentId: newStepId,
          childId: targetStepId,
          parentOutput: 1,
        });
      }
    } else if (context.insertPosition === 'closure' && context.closureOutputs && context.closureOutputs.length > 0) {
      // Branch closure - connect multiple outputs to this new step
      context.closureOutputs.forEach(closureOutput => {
        const parentStep = findStep(closureOutput.stepId);
        if (parentStep) {
          // Update parent's children
          parentStep.children.push({ id: newStepId, output: closureOutput.output });

          // Add to new step's parents
          newStep.parents.push({ id: closureOutput.stepId, output: closureOutput.output });

          // Add connection
          currentFlow.connections.push({
            parentId: closureOutput.stepId,
            childId: newStepId,
            parentOutput: closureOutput.output,
          });
        }
      });

      // Set primary parent to be the deepest one
      if (context.closureOutputs.length > 0) {
         const deepest = findDeepestParent(currentFlow, context.closureOutputs);
         if (deepest) {
           newStep.primaryParent = { id: deepest.stepId, output: deepest.output };
           
           // Reorder parents: deepest first
           const otherParents = newStep.parents.filter(p => !(p.id === deepest.stepId && p.output === deepest.output));
           newStep.parents = [{ id: deepest.stepId, output: deepest.output }, ...otherParents];
         } else {
           const firstOutput = context.closureOutputs[0];
           newStep.primaryParent = { id: firstOutput.stepId, output: firstOutput.output };
         }
      }
    } else if (!currentFlow.rootStepId) {
      // First step (root)
      currentFlow.rootStepId = newStepId;
    }

    // Add the step
    currentFlow.steps.push(newStep);
    currentFlow.metadata.totalSteps = currentFlow.steps.length;
    markDirty();

    // [FLOW_EDITOR_DEBUG] Log add operation
    console.log(LOG_PREFIX, 'ADD_OPERATOR', {
      newStepId,
      operatorId: operator.id,
      operatorKind: operator.kind,
      context,
      targetStepId,
      data,
    });
    console.log(LOG_PREFIX, 'FLOW_STATE_AFTER_ADD', JSON.parse(JSON.stringify(currentFlow)));

    return newStepId;
  };

  /**
   * Update an existing operator's data (merges with existing parameters)
   */
  const updateOperator = (stepId: string, data: Record<string, any>): boolean => {
    const currentFlow = getMutableFlow();
    if (!currentFlow) return false;

    const step = findStep(stepId);
    if (!step) return false;

    // Save state before mutation (debounced for rapid updates)
    saveToHistory('update_operator', { operatorType: step.type, operatorId: stepId });

    // Merge new data with existing parameters
    for (const [key, value] of Object.entries(data)) {
      if (value === null || value === undefined) {
        delete step.parameters[key];
      } else if (typeof value === 'object') {
        step.parameters[key] = JSON.stringify(value);
      } else {
        step.parameters[key] = String(value);
      }
    }
    markDirty();

    // [FLOW_EDITOR_DEBUG] Log update operation
    console.log(LOG_PREFIX, 'UPDATE_OPERATOR', {
      stepId,
      data,
    });
    console.log(LOG_PREFIX, 'FLOW_STATE_AFTER_UPDATE', JSON.parse(JSON.stringify(currentFlow)));

    return true;
  };

  /**
   * Remove an operator from the flow
   */
  const removeOperator = (stepId: string): boolean => {
    const currentFlow = getMutableFlow();
    if (!currentFlow) return false;

    const stepIndex = currentFlow.steps.findIndex(s => s.id === stepId);
    if (stepIndex === -1) return false;

    const step = currentFlow.steps[stepIndex];

    // Save state before mutation
    saveToHistory('remove_operator', { operatorType: step.type, operatorId: stepId });

    // Get all parents of the step being removed
    const allParents = step.parents && step.parents.length > 0 
      ? step.parents 
      : (step.primaryParent ? [step.primaryParent] : []);

    // Get the connection from parent to this step (to get the output index)
    const parentConnection = currentFlow.connections.find(c => c.childId === stepId);
    const parentOutput = parentConnection?.parentOutput ?? 1;

    // Handle reconnection of parents to children
    if (allParents.length > 0 && step.children.length === 1) {
      // Single child case: connect ALL parents to this child
      const childRef = step.children[0];
      const childStep = findStep(childRef.id);
      
      if (childStep) {
        // Remove reference to deleted step from child's parents
        childStep.parents = childStep.parents.filter(p => p.id !== stepId);
        
        // Add all parents of deleted step to child's parents
        allParents.forEach((parentRef) => {
          const parentStep = findStep(parentRef.id);
          if (parentStep) {
            // Update parent's children: replace deleted step with child
            const childIndex = parentStep.children.findIndex(c => c.id === stepId);
            if (childIndex !== -1) {
              parentStep.children[childIndex] = { id: childRef.id, output: parentRef.output };
            }
            
            // Add parent to child's parents list
            childStep.parents.push({ id: parentRef.id, output: parentRef.output });
            
            // Update connection: parent -> deleted step becomes parent -> child
            const connectionToUpdate = currentFlow.connections.find(
              c => c.parentId === parentRef.id && c.childId === stepId
            );
            if (connectionToUpdate) {
              connectionToUpdate.childId = childRef.id;
            }
          }
        });
        
        // Set primaryParent as the first parent
        if (childStep.parents.length > 0) {
          childStep.primaryParent = childStep.parents[0];
        }

        // Remove connection from this step to child
        currentFlow.connections = currentFlow.connections.filter(
          c => !(c.parentId === stepId && c.childId === childRef.id)
        );
      }
    } else if (allParents.length > 0 && step.children.length > 1) {
      // Multiple children: keep leftmost branch, delete all others with their descendants
      const sortedChildren = [...step.children].sort((a, b) => a.output - b.output);
      const leftmostChild = sortedChildren[0];
      const leftmostChildStep = findStep(leftmostChild.id);
      const stepsToDelete: string[] = [];
      
      // Collect all steps to delete (all branches except leftmost)
      sortedChildren.slice(1).forEach((childRef) => {
        stepsToDelete.push(childRef.id);
        const collectAllDescendants = (id: string) => {
          const s = findStep(id);
          if (s) {
            s.children.forEach(c => {
              if (!stepsToDelete.includes(c.id)) {
                stepsToDelete.push(c.id);
                collectAllDescendants(c.id);
              }
            });
          }
        };
        collectAllDescendants(childRef.id);
      });

      // Connect leftmost branch to parent
      if (leftmostChildStep && step.primaryParent) {
        const parentStep = findStep(step.primaryParent.id);
        if (parentStep) {
          const childIndex = parentStep.children.findIndex(c => c.id === stepId);
          if (childIndex !== -1) {
            parentStep.children[childIndex] = leftmostChild;
          }

          leftmostChildStep.primaryParent = step.primaryParent;
          leftmostChildStep.parents = leftmostChildStep.parents.map(p =>
            p.id === stepId ? step.primaryParent! : p
          );

          // Update connection
          const connectionIndex = currentFlow.connections.findIndex(
            c => c.childId === leftmostChild.id && c.parentId === stepId
          );
          if (connectionIndex !== -1) {
            currentFlow.connections[connectionIndex].parentId = step.primaryParent.id;
          }
        }
      }

      // Clean up: remove references to deleted steps from remaining steps
      currentFlow.steps.forEach(s => {
        s.children = s.children.filter(c => !stepsToDelete.includes(c.id));
        s.parents = s.parents.filter(p => !stepsToDelete.includes(p.id));
        if (s.primaryParent && stepsToDelete.includes(s.primaryParent.id)) {
          s.primaryParent = s.parents.length > 0 ? s.parents[0] : undefined;
        }
      });

      // Remove connection from parent to this step
      currentFlow.connections = currentFlow.connections.filter(
        c => c.childId !== stepId
      );

      // Delete steps and their connections
      stepsToDelete.forEach(id => {
        const idx = currentFlow.steps.findIndex(s => s.id === id);
        if (idx !== -1) {
          currentFlow.steps.splice(idx, 1);
        }
        currentFlow.connections = currentFlow.connections.filter(
          c => c.parentId !== id && c.childId !== id
        );
      });
    } else if (allParents.length > 0 && step.children.length === 0) {
      // No children - just remove this step from all parents' children
      allParents.forEach(parent => {
        const parentStep = findStep(parent.id);
        if (parentStep) {
          parentStep.children = parentStep.children.filter(c => c.id !== stepId);
        }
      });

      // Remove all connections involving this step
      currentFlow.connections = currentFlow.connections.filter(
        c => c.parentId !== stepId && c.childId !== stepId
      );
    } else {
      // No parents - just remove connections
      currentFlow.connections = currentFlow.connections.filter(
        c => c.parentId !== stepId && c.childId !== stepId
      );
    }

    // Update root if needed
    if (currentFlow.rootStepId === stepId) {
      currentFlow.rootStepId = step.children[0]?.id ?? null;
    }

    // [FLOW_EDITOR_DEBUG] Log remove operation before removal
    console.log(LOG_PREFIX, 'REMOVE_OPERATOR', {
      stepId,
      stepType: step.type,
      hadParent: !!step.primaryParent,
      childrenCount: step.children.length,
    });
    console.log(LOG_PREFIX, 'FLOW_STATE_BEFORE_REMOVE', JSON.parse(JSON.stringify(currentFlow)));

    // Remove the step
    currentFlow.steps.splice(stepIndex, 1);
    currentFlow.metadata.totalSteps = currentFlow.steps.length;
    markDirty();

    // [FLOW_EDITOR_DEBUG] Log state after removal
    console.log(LOG_PREFIX, 'FLOW_STATE_AFTER_REMOVE', JSON.parse(JSON.stringify(currentFlow)));

    return true;
  };

  /**
   * Remove branches (children) from a specific output of a step
   * Used when decreasing the number of outputs on a filter or ABTest
   */
  const removeBranches = (stepId: string, outputsToRemove: string[]): boolean => {
    const currentFlow = getMutableFlow();
    if (!currentFlow) return false;

    const step = findStep(stepId);
    if (!step) return false;

    console.log(LOG_PREFIX, 'REMOVE_BRANCHES', { stepId, outputsToRemove });

    // Convert output names to output indices (e.g., 'output_2' -> 2)
    const outputIndicesToRemove = outputsToRemove.map(output => {
      const match = output.match(/output_(\d+)/);
      return match ? parseInt(match[1], 10) : null;
    }).filter((idx): idx is number => idx !== null);

    // Find all children connected to these outputs
    const childrenToRemove: string[] = [];
    step.children.forEach(child => {
      if (outputIndicesToRemove.includes(child.output)) {
        childrenToRemove.push(child.id);
      }
    });

    // Remove children from step's children array
    step.children = step.children.filter(child => !outputIndicesToRemove.includes(child.output));

    // Remove connections
    currentFlow.connections = currentFlow.connections.filter(conn => {
      if (conn.parentId === stepId && outputIndicesToRemove.includes(conn.parentOutput)) {
        return false;
      }
      return true;
    });

    // Recursively remove all descendant steps
    const removeStepAndDescendants = (removeStepId: string) => {
      const stepToRemove = findStep(removeStepId);
      if (!stepToRemove) return;

      // First, recursively remove all children
      const childIds = stepToRemove.children.map(c => c.id);
      childIds.forEach(childId => removeStepAndDescendants(childId));

      // Remove from parent's children
      stepToRemove.parents.forEach(parent => {
        const parentStep = findStep(parent.id);
        if (parentStep) {
          parentStep.children = parentStep.children.filter(c => c.id !== removeStepId);
        }
      });

      // Remove connections involving this step
      currentFlow.connections = currentFlow.connections.filter(
        conn => conn.parentId !== removeStepId && conn.childId !== removeStepId
      );

      // Remove the step itself
      const idx = currentFlow.steps.findIndex(s => s.id === removeStepId);
      if (idx !== -1) {
        currentFlow.steps.splice(idx, 1);
      }
    };

    // Remove each child branch
    childrenToRemove.forEach(childId => removeStepAndDescendants(childId));

    currentFlow.metadata.totalSteps = currentFlow.steps.length;
    markDirty();

    console.log(LOG_PREFIX, 'FLOW_STATE_AFTER_REMOVE_BRANCHES', JSON.parse(JSON.stringify(currentFlow)));

    return true;
  };

  /**
   * Add a connection from an output to an existing step's input (for branch merge)
   * This creates a secondary parent for the target step
   */
  const addConnection = (parentId: string, parentOutput: number, childId: string): boolean => {
    const currentFlow = getMutableFlow();
    if (!currentFlow) return false;

    const parentStep = findStep(parentId);
    const childStep = findStep(childId);
    if (!parentStep || !childStep) return false;

    // Check if connection already exists
    const existingConnection = currentFlow.connections.find(
      c => c.parentId === parentId && c.childId === childId && c.parentOutput === parentOutput
    );
    if (existingConnection) return false;

    // Save state before mutation (merge branches)
    saveToHistory('merge_branches', {
      sourceOperatorType: parentStep.type,
      targetOperatorType: childStep.type,
    });

    // Add to parent's children
    if (!parentStep.children.find(c => c.id === childId && c.output === parentOutput)) {
      parentStep.children.push({ id: childId, output: parentOutput });
    }

    // Add to child's parents (as secondary parent)
    if (!childStep.parents.find(p => p.id === parentId && p.output === parentOutput)) {
      childStep.parents.push({ id: parentId, output: parentOutput });
    }

    // Add connection
    currentFlow.connections.push({
      parentId,
      childId,
      parentOutput,
    });

    markDirty();

    console.log(LOG_PREFIX, 'ADD_CONNECTION', {
      parentId,
      parentOutput,
      childId,
    });
    console.log(LOG_PREFIX, 'FLOW_STATE_AFTER_ADD_CONNECTION', JSON.parse(JSON.stringify(currentFlow)));

    return true;
  };

  /**
   * Remove a connection between two steps (for unlinking merged branches)
   * Only allowed if the child step has multiple parents
   */
  const removeConnection = (parentId: string, parentOutput: number, childId: string): boolean => {
    const currentFlow = getMutableFlow();
    if (!currentFlow) return false;

    const parentStep = findStep(parentId);
    const childStep = findStep(childId);
    if (!parentStep || !childStep) return false;

    // Only allow removal if child has multiple parents
    if (childStep.parents.length <= 1) {
      console.log(LOG_PREFIX, 'REMOVE_CONNECTION_DENIED', 'Child has only one parent');
      return false;
    }

    // Save state before mutation
    saveToHistory('unmerge_branches', {
      sourceOperatorType: parentStep.type,
      targetOperatorType: childStep.type,
    });

    // Remove from parent's children
    parentStep.children = parentStep.children.filter(
      c => !(c.id === childId && c.output === parentOutput)
    );

    // Remove from child's parents
    childStep.parents = childStep.parents.filter(
      p => !(p.id === parentId && p.output === parentOutput)
    );

    // Update primaryParent if it was the removed one
    if (childStep.primaryParent?.id === parentId && childStep.primaryParent?.output === parentOutput) {
      childStep.primaryParent = childStep.parents.length > 0 ? childStep.parents[0] : undefined;
    }

    // Remove connection
    currentFlow.connections = currentFlow.connections.filter(
      c => !(c.parentId === parentId && c.childId === childId && c.parentOutput === parentOutput)
    );

    markDirty();

    console.log(LOG_PREFIX, 'REMOVE_CONNECTION', {
      parentId,
      parentOutput,
      childId,
    });
    console.log(LOG_PREFIX, 'FLOW_STATE_AFTER_REMOVE_CONNECTION', JSON.parse(JSON.stringify(currentFlow)));

    return true;
  };

  /**
   * Add a new operator with multiple parents (for branch closure)
   * Creates a step that merges multiple branches into one
   */
  const addOperatorWithMultipleParents = (
    operator: FlowOperatorMetadata,
    data: Record<string, any>,
    parents: Array<{ stepId: string; output: number }>
  ): string | null => {
    const currentFlow = getMutableFlow();
    if (!currentFlow || parents.length === 0) return null;

    // Note: This function is not currently used - closure operations go through addOperator
    // If used directly, history should be saved by the caller

    const newStepId = generateNextOperatorId(currentFlow);

    // Create the new step with multiple parents
    const newStep: FlowStepDto = {
      id: newStepId,
      type: operator.id,
      class: kindToClass(operator.kind),
      parents: parents.map(p => ({ id: p.stepId, output: p.output })),
      children: [],
      parameters: dataToParameters(operator.id, data),
    };

    // Set primary parent to be the deepest one
    const deepest = findDeepestParent(currentFlow, parents);
    if (deepest) {
      newStep.primaryParent = { id: deepest.stepId, output: deepest.output };
      
      // Reorder parents: deepest first
      const otherParents = newStep.parents.filter(p => !(p.id === deepest.stepId && p.output === deepest.output));
      newStep.parents = [{ id: deepest.stepId, output: deepest.output }, ...otherParents];
    } else {
      newStep.primaryParent = { id: parents[0].stepId, output: parents[0].output };
    }

    // Update all parent steps and create connections
    parents.forEach(parent => {
      const parentStep = findStep(parent.stepId);
      if (parentStep) {
        parentStep.children.push({ id: newStepId, output: parent.output });
      }

      currentFlow.connections.push({
        parentId: parent.stepId,
        childId: newStepId,
        parentOutput: parent.output,
      });
    });

    // Add the step
    currentFlow.steps.push(newStep);
    currentFlow.metadata.totalSteps = currentFlow.steps.length;
    markDirty();

    console.log(LOG_PREFIX, 'ADD_OPERATOR_WITH_MULTIPLE_PARENTS', {
      newStepId,
      operatorId: operator.id,
      parents,
    });
    console.log(LOG_PREFIX, 'FLOW_STATE_AFTER_ADD_MULTI_PARENT', JSON.parse(JSON.stringify(currentFlow)));

    return newStepId;
  };

  /**
   * Replace an operator with a new one (keeps connections)
   */
  const replaceOperator = (stepId: string, operator: FlowOperatorMetadata, data: any): boolean => {
    const currentFlow = getMutableFlow();
    if (!currentFlow) return false;

    const step = findStep(stepId);
    if (!step) return false;

    // Save state before mutation
    saveToHistory('replace_operator', {
      oldOperatorType: step.type,
      newOperatorType: operator.id,
    });

    // [FLOW_EDITOR_DEBUG] Log replace operation
    const oldType = step.type;
    const oldParameters = step.parameters ?? {};

    // class must follow the new kind: the engine dispatches on it and parks a
    // workflow forever on a step left in class 'declencheur'
    step.type = operator.id;
    step.class = kindToClass(operator.kind);
    step.parameters = dataToParameters(operator.id, data);

    // dataToParameters drops the webhook: keep it when the trigger is unchanged,
    // otherwise a new one is created on save and the old one stays armed
    const isSameTrigger = operator.kind === 'trigger'
      && oldType === operator.id
      && String(oldParameters.event ?? '') === String(step.parameters.event ?? '');
    if (isSameTrigger && oldParameters.webhook && !step.parameters.webhook) {
      step.parameters.webhook = oldParameters.webhook;
    }

    markDirty();

    console.log(LOG_PREFIX, 'REPLACE_OPERATOR', {
      stepId,
      oldType,
      newType: operator.id,
      newKind: operator.kind,
      data,
    });
    console.log(LOG_PREFIX, 'FLOW_STATE_AFTER_REPLACE', JSON.parse(JSON.stringify(currentFlow)));

    return true;
  };

  /**
   * Reorder branches (outputs) of a step
   * @param stepId The step ID to reorder
   * @param newOrder Array of original output indices in their new order
   */
  const reorderBranches = (stepId: string, newOrder: number[]): boolean => {
    const currentFlow = getMutableFlow();
    if (!currentFlow) return false;

    const step = findStep(stepId);
    if (!step) return false;

    // Save state before mutation
    saveToHistory('reorder_branches', { operatorType: step.type, operatorId: stepId });

    // Create a map from old output index to new output index
    const oldToNew = new Map<number, number>();
    newOrder.forEach((oldOutput, newIndex) => {
      oldToNew.set(oldOutput, newIndex + 1); // 1-based indexing
    });

    // 1. Update Connections
    currentFlow.connections.forEach(conn => {
      if (conn.parentId === stepId) {
        const newOutput = oldToNew.get(conn.parentOutput);
        if (newOutput) {
          conn.parentOutput = newOutput;
        }
      }
    });

    // 2. Update Step Children references (in the parent step)
    if (step.children) {
      step.children.forEach(child => {
        const newOutput = oldToNew.get(child.output);
        if (newOutput) {
          child.output = newOutput;
        }
      });
      // Re-sort children array to match new output order
      step.children.sort((a, b) => a.output - b.output);
    }

    // 3. Update Child Steps (parents and primaryParent references)
    currentFlow.steps.forEach(s => {
      // Update parents array
      if (s.parents) {
        s.parents.forEach(parent => {
          if (parent.id === stepId) {
            const newOutput = oldToNew.get(parent.output);
            if (newOutput) {
              parent.output = newOutput;
            }
          }
        });
      }

      // Update primaryParent
      if (s.primaryParent && s.primaryParent.id === stepId) {
        const newOutput = oldToNew.get(s.primaryParent.output);
        if (newOutput) {
          s.primaryParent.output = newOutput;
        }
      }
    });

    markDirty();

    console.log(LOG_PREFIX, 'REORDER_BRANCHES', { stepId, newOrder });

    return true;
  };

  return {
    // State (readonly computed)
    flow,
    isDirty,

    // History state
    canUndo: history.canUndo,
    canRedo: history.canRedo,
    undoActionType: history.undoActionType,
    undoMeta: history.undoMeta,
    redoActionType: history.redoActionType,
    redoMeta: history.redoMeta,
    undoStack: history.undoStack,
    redoStack: history.redoStack,

    // Store access (for multi-flow features)
    store,
    flowIdentifier,

    // Initialization (standalone mode)
    initFlow,
    resetFlow,
    getFlowForSave,

    // Operations
    addOperator,
    updateOperator,
    removeOperator,
    removeBranches,
    replaceOperator,
    findStep,

    // Branch operations
    addConnection,
    removeConnection,
    addOperatorWithMultipleParents,
    reorderBranches,

    // History operations
    undo,
    redo,
    restoreToIndex,
    clearHistory: history.clear,
    createSaveCheckpoint,
  };
}

export type FlowEditor = ReturnType<typeof useFlowEditor>;
